import { type FormEvent, useMemo, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { AdminTable } from "../../components/admin/AdminTable";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useInvites } from "../../hooks/useInvites";
import { useTenants } from "../../hooks/useTenants";
import { formatDateTime, statusTone, titleCase } from "../../lib/format";
import { inviteService } from "../../services/inviteService";
import type { TenantPlan } from "../../types/admin";

type CreatedInvite = {
  link: string;
  email: string;
  name: string;
  workspaceName: string;
};

function getActionError(error: unknown, fallback: string) {
  const message = error instanceof Error
    ? error.message
    : error && typeof error === "object" && "message" in error
      ? String(error.message)
      : "";

  if (!message) return fallback;

  if (/column reference|ambiguous|SQLSTATE|schema cache|function .*does not exist|relation .*does not exist/i.test(message)) {
    return fallback;
  }

  return message;
}

function logActionError(context: string, error: unknown) {
  if (import.meta.env.DEV) {
    console.error(context, error);
  }
}

function getInviteEmailHref(input: {
  email: string;
  link: string;
  name?: string | null;
  workspaceName?: string | null;
}) {
  const greeting = input.name?.trim() ? `Hi ${input.name.trim()},` : "Hi,";
  const workspaceLine = input.workspaceName?.trim()
    ? `You've been invited to join ${input.workspaceName.trim()} on CoachGrid.`
    : "You've been invited to join CoachGrid.";

  const subject = "Your CoachGrid invite";
  const body = [
    greeting,
    "",
    workspaceLine,
    "Use this secure link to activate your account:",
    input.link,
    "",
    "This invite is single-use and may expire.",
  ].join("\n");

  return `mailto:${encodeURIComponent(input.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function AdminInvitesPage() {
  const [mode, setMode] = useState<"new_workspace" | "existing_workspace">("new_workspace");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [tenantId, setTenantId] = useState("all");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [plan, setPlan] = useState<TenantPlan | "trial">("trial");
  const [inviteRole, setInviteRole] = useState("tenant_owner");
  const [createdInvite, setCreatedInvite] = useState<CreatedInvite | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const filters = useMemo(() => ({ search, status, role, tenantId }), [search, status, role, tenantId]);
  const { data: invites, loading, error, refresh } = useInvites(filters);
  const { data: tenants } = useTenants("");

  async function createInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError(null);
    setCopiedLink(null);
    setCreatedInvite(null);
    setSubmitting(true);
    try {
      const inviteEmail = email.trim();
      const inviteName = name.trim();
      const inviteWorkspaceName = workspaceName.trim();
      const result = mode === "new_workspace"
        ? await inviteService.createInvite({
            mode,
            invitedEmail: inviteEmail,
            invitedName: inviteName,
            workspaceName: inviteWorkspaceName,
            plan: plan === "trial" ? "enterprise" : plan,
            role: "tenant_owner",
            trialDays: plan === "trial" ? 14 : null,
          })
        : await inviteService.createInvite({
            mode,
            tenantId,
            invitedEmail: inviteEmail,
            invitedName: inviteName,
            role: inviteRole as "tenant_owner" | "trainer" | "assistant",
          });

      if (result?.token) {
        const selectedTenant = tenants?.find((tenant) => tenant.id === tenantId);
        setCreatedInvite({
          link: inviteService.getInviteLink(result.token),
          email: inviteEmail,
          name: inviteName,
          workspaceName:
            mode === "new_workspace"
              ? inviteWorkspaceName
              : selectedTenant?.name ?? "",
        });
      }

      setEmail("");
      setName("");
      setWorkspaceName("");
      await refresh();
    } catch (caughtError) {
      logActionError("Unable to create invite.", caughtError);
      setActionError(getActionError(caughtError, "Unable to create invite."));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyInviteLink(link: string) {
    setActionError(null);

    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
    } catch {
      setActionError("Unable to copy the invite link. Select the link text and copy it manually.");
    }
  }

  async function cancelInvite(inviteId: string) {
    setActionError(null);
    try {
      await inviteService.cancelInvite(inviteId);
      await refresh();
    } catch (caughtError) {
      logActionError("Unable to cancel invite.", caughtError);
      setActionError(getActionError(caughtError, "Unable to cancel invite."));
    }
  }

  return (
    <div>
      <AdminPageHeader title="Invites" description="Create, track, copy and cancel invite-only account activation links." />

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <AdminPanel title="Create invite">
          <form className="space-y-4" onSubmit={createInvite}>
            <div className="grid grid-cols-2 rounded-md border border-line bg-white p-1">
              <button className={`rounded px-3 py-2 text-sm font-semibold ${mode === "new_workspace" ? "bg-ink text-white" : "text-graphite"}`} type="button" onClick={() => {
                setMode("new_workspace");
                setInviteRole("tenant_owner");
              }}>New workspace</button>
              <button className={`rounded px-3 py-2 text-sm font-semibold ${mode === "existing_workspace" ? "bg-ink text-white" : "text-graphite"}`} type="button" onClick={() => setMode("existing_workspace")}>Existing workspace</button>
            </div>
            <Input label="Invitee name" value={name} onChange={(event) => setName(event.target.value)} />
            <Input label="Invitee email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            {mode === "new_workspace" ? (
              <>
                <Input label="Workspace/business name" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} required />
                <label className="block text-sm font-medium text-ink">Plan
                  <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={plan} onChange={(event) => setPlan(event.target.value as TenantPlan | "trial")}>
                    <option value="trial">14-day trial - enterprise access</option>
                    {["starter", "pro", "studio", "enterprise"].map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
                <p className="rounded-md border border-line bg-field px-3 py-2 text-sm text-graphite">
                  New workspaces are created with an owner. Trainers can be invited after the workspace exists.
                </p>
              </>
            ) : (
              <>
                <label className="block text-sm font-medium text-ink">Workspace
                  <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={tenantId} onChange={(event) => setTenantId(event.target.value)} required>
                    <option value="all" disabled>Select workspace</option>
                    {tenants?.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-ink">Role
                  <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={inviteRole} onChange={(event) => setInviteRole(event.target.value)}>
                    {["tenant_owner", "trainer", "assistant"].map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
              </>
            )}
            <Button fullWidth isLoading={submitting} type="submit">Create invite</Button>
          </form>
          {createdInvite ? (
            <div className="mt-5 rounded-md border border-pine/20 bg-mint p-4 text-sm text-pine">
              <p className="font-semibold">Invite link ready</p>
              <div className="mt-3 rounded-md border border-pine/20 bg-white p-3 text-ink">
                <p className="break-all text-sm leading-6">{createdInvite.link}</p>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={() => void copyInviteLink(createdInvite.link)}
                >
                  {copiedLink === createdInvite.link ? "Copied" : "Copy link"}
                </Button>
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={() => {
                    window.location.href = getInviteEmailHref(createdInvite);
                  }}
                >
                  Email invite
                </Button>
              </div>
            </div>
          ) : null}
          {actionError ? <p className="mt-4 text-sm font-medium text-red-900">{actionError}</p> : null}
        </AdminPanel>

        <div>
          <div className="mb-5 grid gap-3 md:grid-cols-4">
            <Input label="Search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Email" />
            <label className="text-sm font-medium text-ink">Status
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value)}>
                {["all", "pending", "accepted", "expired", "cancelled"].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-ink">Role
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={role} onChange={(event) => setRole(event.target.value)}>
                {["all", "platform_admin", "tenant_owner", "trainer", "assistant", "client"].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-ink">Tenant
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
                <option value="all">All</option>
                {tenants?.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
              </select>
            </label>
          </div>

          {loading ? <FullPageLoader label="Loading invites" /> : null}
          {error ? <EmptyState title="Unable to load invites.">{error}</EmptyState> : null}
          {!loading && !error && invites ? (
            <AdminTable headers={["Email", "Workspace", "Role", "Status", "Expires", "Link", "Actions"]}>
              {invites.map((invite) => (
                <tr key={invite.id}>
                  <td className="px-4 py-3">{invite.invited_email}</td>
                  <td className="px-4 py-3">{invite.tenants?.name ?? "Platform"}</td>
                  <td className="px-4 py-3">{titleCase(invite.role)}</td>
                  <td className="px-4 py-3"><Badge tone={statusTone(invite.status)}>{invite.status}</Badge></td>
                  <td className="px-4 py-3">{formatDateTime(invite.expires_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => void copyInviteLink(inviteService.getInviteLink(invite.token))}
                      >
                        {copiedLink === inviteService.getInviteLink(invite.token) ? "Copied" : "Copy"}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          window.location.href = getInviteEmailHref({
                            email: invite.invited_email,
                            link: inviteService.getInviteLink(invite.token),
                            name: invite.invited_name,
                            workspaceName: invite.tenants?.name ?? null,
                          });
                        }}
                      >
                        Email
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {invite.status === "pending" ? <Button variant="secondary" onClick={() => void cancelInvite(invite.id)}>Cancel</Button> : <span className="text-graphite/50">No action</span>}
                  </td>
                </tr>
              ))}
            </AdminTable>
          ) : null}
        </div>
      </div>
    </div>
  );
}
